import type { ReactNode, FC } from 'react';
import { useContext, useMemo } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  PapiGallection,
  PapiPremiumFolderData,
  PapiSellerDiscount,
} from '@wix/da-papi-types';
import { OverlayContextProvider } from '@wix/da-react-context/pkg/OverlayContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import DeviationDumbView from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import ExcerptRenderer from '@wix/da-shared-react/pkg/TextRenderer/ExcerptRenderer';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import CollapsibleBlock from '@wix/da-shared-react/pkg/CollapsibleBlock';
import Button from '@wix/da-shared-react/pkg/Button';
import PremiumGalleryIcon from '@wix/da-ds/pkg/Icons/24x24/PremiumGallery';
import DiscountsPopupTrigger from '@wix/da-shared-react/pkg/DiscountsPopupTrigger';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import GallectionTiptapViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/Gallection';
import PremiumFolderPrice from './PremiumFolderPrice';
import FolderInfo from './FolderInfo';

import s from './PremiumGalleryPaywallLayout.scss';

export interface Props {
  className?: string;
  icon?: ReactNode;
  folder: PapiGallection;
  button: ReactNode;
  nonClickable?: boolean;
  premiumFolderData: PapiPremiumFolderData;
}

const COLLAPSE_WHEN_THIS_TALL = 70;

const PremiumGalleryPaywallLayout: FC<Props> = ({
  icon = null,
  folder,
  button,
  nonClickable = false,
}) => {
  const { t } = useTranslation();
  const {
    numSubscribers,
    isPaidFolder,
    isWatchToAccess,
    eligibleDiscount,
    notEligibleDiscounts,
  } = normalizePremiumFolderData(folder.premiumFolderData);
  const allDiscounts = useMemo(() => {
    return [eligibleDiscount, ...(notEligibleDiscounts || [])].filter(
      Boolean
    ) as PapiSellerDiscount[];
  }, [eligibleDiscount, notEligibleDiscounts]);

  const itemCount = folder.totalItemCount ?? folder.size;
  const thumb = folder.thumb ?? folder?.additionalThumbs?.[0];
  const isMobile = useContext(MobileContext);
  const { width: screenWidth } = useContext(ScreenSizeContext);
  const mobileThumbWidth = screenWidth - 2 * 32;

  return (
    <OverlayContextProvider
      value={{
        showPremiumFolderOverlay: false,
        showPremiumFolderIcon: false,
        showThumbStatusIndicators: false,
      }}
    >
      <div className={s['root']}>
        <div className={s['content']}>
          <div className={s['cover-wrap']}>
            {thumb && (
              <>
                {icon && <div className={s['cover-icon']}>{icon}</div>}
                <DeviationDumbView
                  className={s['cover-image']}
                  deviation={thumb}
                  width={isMobile ? mobileThumbWidth : 375}
                  height={250}
                  useCoverMode={true}
                />
              </>
            )}
          </div>
          <div className={s['info-wrap']}>
            <div className={s['info-header']}>
              {(isPaidFolder || isWatchToAccess) && (
                <div className={s['info-header--top']}>
                  <PremiumGalleryIcon className={s['folder-icon']} />
                  <div className={s['folder-type']}>
                    {t(
                      isPaidFolder
                        ? 'paywall.premium.folder.title'
                        : 'paywall.watchersOnly.folder.title'
                    )}
                  </div>
                </div>
              )}

              <div className={s['info-header--middle']}>
                <div className={s['title']}>
                  <div className={s['folder-name']}>{folder.name}</div>
                </div>
                <PremiumFolderPrice
                  className={s['discount-price']}
                  folder={folder}
                />
              </div>

              <div className={s['info-header--bottom']}>
                <FolderInfo
                  className={s['folder-info']}
                  deviationCount={itemCount}
                  numSubscribers={numSubscribers}
                />
                <DiscountsPopupTrigger
                  className={s['discounts']}
                  allDiscounts={allDiscounts}
                  eligibleDiscount={eligibleDiscount}
                />
              </div>
            </div>
            <div className={s['info-body']}>
              {folder?.richDescription && (
                <div className={s['text']}>
                  <CollapsibleBlock
                    maxHeight={COLLAPSE_WHEN_THIS_TALL}
                    showFade
                    fadeClassName={s['collapsible-fade']}
                    renderToggleButton={({ collapsed, onClick }) => (
                      <Button
                        view="b3"
                        className={s['collapsible-button']}
                        onClick={onClick as any}
                      >
                        {t(
                          collapsed
                            ? 'pages.gallections.folderDescription.readMore'
                            : 'common.showLess'
                        )}
                      </Button>
                    )}
                  >
                    {folder?.richDescription?.html.type === 'writer' && (
                      <ExcerptRenderer
                        className={s['description']}
                        htmlExcerptLegacy
                        legacyTheme={'comments'}
                        textContent={folder.richDescription}
                      />
                    )}
                    {folder?.richDescription?.html.type === 'tiptap' && (
                      <TextRenderer
                        className={s['description']}
                        tipTapPresetId="gallection"
                        tipTapViewer={GallectionTiptapViewer}
                        cookieType={
                          MeasuredCookieType.GALLECTION_FOLDER_PAYWALL
                        }
                        defaultSize={{
                          desktop: { width: 470, height: 800 },
                          mobile: { width: 380, height: 400 },
                        }}
                        textContent={folder.richDescription}
                      />
                    )}
                  </CollapsibleBlock>
                </div>
              )}
            </div>
            <div className={s['info-footer']}>
              <span
                className={c(
                  s['button'],
                  nonClickable && s['non-clickable-button']
                )}
              >
                {button}
              </span>
            </div>
          </div>
        </div>
      </div>
    </OverlayContextProvider>
  );
};
PremiumGalleryPaywallLayout.displayName = 'PremiumGalleryPaywallLayout';

export default PremiumGalleryPaywallLayout;
